import { useState, useCallback, useEffect, useRef } from 'react';
import * as dbOps from '../db/kioskDbOperations';

const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes (fallback polling)
const INSTANT_SYNC_DELAY_MS = 1500; // debounce before instant sync
const BATCH_SIZE = 50; // events per sync batch

/**
 * Sync engine for offline-first kiosk.
 *
 * Manages background synchronisation of pending clock events from
 * IndexedDB to the server via the kiosk-auth edge function.
 *
 * @param {Function} apiCall - The shared API helper from useKioskAuth
 */
export function useSyncEngine(apiCall) {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ total: 0, completed: 0, failed: 0 });
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [autoSync, setAutoSync] = useState(true);

  const syncingRef = useRef(false);
  const intervalRef = useRef(null);
  const instantSyncTimer = useRef(null);
  const apiCallRef = useRef(apiCall);
  apiCallRef.current = apiCall;

  // ---------------------------------------------------------------
  // Load config from IndexedDB on mount
  // ---------------------------------------------------------------
  useEffect(() => {
    (async () => {
      const [storedAutoSync, storedLastSync, count] = await Promise.all([
        dbOps.getConfig('auto_sync'),
        dbOps.getConfig('last_sync_time'),
        dbOps.getPendingCount(),
      ]);
      if (storedAutoSync !== null) setAutoSync(storedAutoSync);
      if (storedLastSync) setLastSyncTime(new Date(storedLastSync));
      setPendingCount(count);
    })();
  }, []);

  // ---------------------------------------------------------------
  // Refresh pending count (call after any clock event)
  // ---------------------------------------------------------------
  const refreshPendingCount = useCallback(async () => {
    const count = await dbOps.getPendingCount();
    setPendingCount(count);
  }, []);

  // ---------------------------------------------------------------
  // Core sync function
  // ---------------------------------------------------------------
  const sync = useCallback(async () => {
    if (syncingRef.current) return { skipped: true };
    if (!navigator.onLine) return { success: false, error: 'Offline' };
    if (!apiCallRef.current) return { success: false, error: 'No API connection' };

    syncingRef.current = true;
    setIsSyncing(true);
    const startTime = Date.now();

    const summary = { total: 0, synced: 0, failed: 0, conflicts: 0 };

    try {
      const pendingEvents = await dbOps.getPendingEvents();

      if (pendingEvents.length === 0) {
        // Nothing to sync, but still refresh employees
        await refreshEmployeesFromServer();
        await dbOps.setConfig('last_sync_time', new Date().toISOString());
        const now = new Date();
        setLastSyncTime(now);
        return { success: true, summary };
      }

      summary.total = pendingEvents.length;
      setSyncProgress({ total: summary.total, completed: 0, failed: 0 });

      // Process in batches
      for (let i = 0; i < pendingEvents.length; i += BATCH_SIZE) {
        const batch = pendingEvents.slice(i, i + BATCH_SIZE);

        // Build events payload — attach selfie data for clock_in events
        const eventsPayload = await Promise.all(
          batch.map(async (event) => {
            const payload = {
              type: event.action,
              user_id: event.userId,
              timestamp: event.timestamp,
              temp_id: event.tempId,
            };

            if (event.action === 'clock_in') {
              if (event.method) payload.method = event.method;
              if (event.hasSelfie) {
                const selfieData = await dbOps.getSelfie(event.tempId);
                console.log('[SyncEngine] Selfie lookup:', { tempId: event.tempId, hasSelfie: event.hasSelfie, foundSelfie: !!selfieData, selfieLength: selfieData?.length || 0 });
                if (selfieData) payload.selfie_base64 = selfieData;
              } else {
                console.log('[SyncEngine] Clock-in event has no selfie flag:', { tempId: event.tempId, hasSelfie: event.hasSelfie });
              }
            }

            if (event.action === 'break_start' && event.breakTypeId) {
              payload.break_type_id = event.breakTypeId;
            }

            return payload;
          })
        );

        // Send batch to server
        const result = await apiCallRef.current('sync', { events: eventsPayload });

        if (result.success && result.results) {
          // Mark events based on server results
          await dbOps.markEventsSynced(result.results);

          const batchSynced = result.results.filter((r) => r.status === 'success').length;
          const batchFailed = result.results.filter((r) => r.status === 'error').length;
          const batchConflict = result.results.filter((r) => r.status === 'conflict').length;

          summary.synced += batchSynced;
          summary.failed += batchFailed;
          summary.conflicts += batchConflict;
        } else {
          // Entire batch failed
          summary.failed += batch.length;
        }

        setSyncProgress({
          total: summary.total,
          completed: summary.synced + summary.conflicts,
          failed: summary.failed,
        });
      }

      // Clean up synced selfies
      await dbOps.deleteSyncedSelfies();

      // Refresh employees from server
      await refreshEmployeesFromServer();

      // Update sync time
      const now = new Date();
      await dbOps.setConfig('last_sync_time', now.toISOString());
      setLastSyncTime(now);

      // Log the sync
      const durationMs = Date.now() - startTime;
      await dbOps.addSyncLog({
        status: summary.failed > 0 ? 'partial' : 'success',
        eventsTotal: summary.total,
        eventsSynced: summary.synced,
        eventsFailed: summary.failed,
        eventsConflict: summary.conflicts,
        durationMs,
      });

      // Refresh pending count
      await refreshPendingCount();

      return { success: true, summary };
    } catch (err) {
      const durationMs = Date.now() - startTime;
      await dbOps.addSyncLog({
        status: 'failed',
        eventsTotal: summary.total,
        eventsSynced: summary.synced,
        eventsFailed: summary.failed,
        eventsConflict: summary.conflicts,
        durationMs,
        error: err?.message,
      });

      return { success: false, error: err?.message, summary };
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
      await refreshPendingCount();
    }
  }, [refreshPendingCount]);

  // ---------------------------------------------------------------
  // Refresh employees from server (used during sync)
  // ---------------------------------------------------------------
  const refreshEmployeesFromServer = useCallback(async () => {
    if (!navigator.onLine || !apiCallRef.current) return;

    try {
      const result = await apiCallRef.current('heartbeat');
      if (result.success) {
        if (result.employees?.length) {
          await dbOps.upsertEmployees(result.employees);
          await dbOps.setConfig('last_employee_sync', new Date().toISOString());
          // Download avatars in background
          const localEmps = await dbOps.getAllEmployees();
          dbOps.downloadAndCacheAvatars(localEmps);
        }
        if (result.break_types?.length) {
          await dbOps.upsertBreakTypes(result.break_types);
        }
      }
    } catch {
      // Non-fatal
    }
  }, []);

  // ---------------------------------------------------------------
  // Toggle auto-sync
  // ---------------------------------------------------------------
  const toggleAutoSync = useCallback(async (enabled) => {
    setAutoSync(enabled);
    await dbOps.setConfig('auto_sync', enabled);
  }, []);

  // ---------------------------------------------------------------
  // Request an instant sync (debounced).
  // Call this after every clock event so data syncs immediately
  // when online, without flooding the server on rapid actions.
  // ---------------------------------------------------------------
  const requestSync = useCallback(() => {
    if (!autoSync || !navigator.onLine) return;

    // Clear any pending debounce timer
    if (instantSyncTimer.current) {
      clearTimeout(instantSyncTimer.current);
    }

    instantSyncTimer.current = setTimeout(() => {
      instantSyncTimer.current = null;
      sync();
    }, INSTANT_SYNC_DELAY_MS);
  }, [autoSync, sync]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (instantSyncTimer.current) {
        clearTimeout(instantSyncTimer.current);
      }
    };
  }, []);

  // ---------------------------------------------------------------
  // Connectivity listeners
  // ---------------------------------------------------------------
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when connectivity restored
      if (autoSync) {
        sync();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [autoSync, sync]);

  // ---------------------------------------------------------------
  // Auto-sync interval
  // ---------------------------------------------------------------
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (autoSync) {
      intervalRef.current = setInterval(() => {
        if (navigator.onLine) {
          sync();
        }
      }, SYNC_INTERVAL_MS);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoSync, sync]);

  return {
    // State
    isOnline,
    pendingCount,
    isSyncing,
    syncProgress,
    lastSyncTime,
    autoSync,

    // Actions
    sync,
    requestSync,
    refreshPendingCount,
    toggleAutoSync,
    refreshEmployeesFromServer,
  };
}
